import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useShallow } from "zustand/react/shallow";

import { SelectAllOption } from "ds/components/Table/types";
import { SearchQueryPredicate, Stack, StackState } from "types/generated";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { StackSuggestions } from "constants/stack";

import useStacksTableSelectionStore from "./useStacksTableSelectionStore";
import { MINIMAL_SEARCH_STACKS } from "../gql";
import useFiltersVariables from "../useFiltersVariables";

type UseStacksSelectionOptionsResult = {
  selectionOptions: SelectAllOption[];
  stacksMap: Map<string, Stack>;
};

const createStacksMap = (stacks: Stack[]): Map<string, Stack> => {
  const map = new Map<string, Stack>();
  for (const stack of stacks) {
    map.set(stack.id, stack);
  }
  return map;
};

export const useStacksSelectionOptions = (
  visibleStacks: Stack[]
): UseStacksSelectionOptionsResult => {
  const [stacksMap, setStacksMap] = useState<Map<string, Stack>>(() =>
    createStacksMap(visibleStacks)
  );

  const { replaceSelected, setLoading, setRefetchSelected, addSelected } =
    useStacksTableSelectionStore(
      useShallow((state) => ({
        addSelected: state.addSelected,
        replaceSelected: state.replaceSelected,
        setLoading: state.setLoading,
        setRefetchSelected: state.setRefetchSelected,
      }))
    );

  const visibleItemsIds = useMemo(() => visibleStacks.map((stack) => stack.id), [visibleStacks]);
  const addVisibleItems = useCallback(async () => {
    addSelected(visibleItemsIds);
  }, [addSelected, visibleItemsIds]);

  const { onError } = useTypedContext(FlashContext);
  const { filterVariables } = useFiltersVariables();
  const [fetchStacks] = useLazyQuery(MINIMAL_SEARCH_STACKS);

  const onAllStacksFetched = useCallback(
    (stacks: Stack[]) => {
      setStacksMap(createStacksMap(stacks));
      replaceSelected(stacks.map((stack) => stack.id));
    },
    [replaceSelected]
  );

  const fetchAllStacks = useCallback(
    async (predicates: SearchQueryPredicate[]) => {
      setLoading(true);

      try {
        const allStacks: Stack[] = [];

        const fetchPage = async (after: string | null): Promise<void> => {
          const { data } = await fetchStacks({
            variables: {
              input: {
                after,
                fullTextSearch: filterVariables.searchInput,
                predicates: [...filterVariables.predicates, ...predicates],
                first: 50,
                requestedPage: null,
                ...(filterVariables.sortOptionFields && {
                  orderBy: filterVariables.sortOptionFields,
                }),
              },
            },
            fetchPolicy: "no-cache",
          });

          if (!data?.searchStacks) throw new Error("No data received");

          const pageData = data.searchStacks.edges.map((edge) => edge.node);
          allStacks.push(...pageData);

          if (data.searchStacks.pageInfo.hasNextPage) {
            return await fetchPage(data.searchStacks.pageInfo.endCursor);
          }

          //called only on final recursion
          onAllStacksFetched(allStacks);
        };

        await fetchPage(null);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchStacks,
      filterVariables.predicates,
      filterVariables.searchInput,
      filterVariables.sortOptionFields,
      onAllStacksFetched,
      setLoading,
      onError,
    ]
  );

  const selectAllItems = useCallback(async () => {
    const asyncCall = () => fetchAllStacks([]);

    await asyncCall();
    setRefetchSelected(asyncCall);
  }, [fetchAllStacks, setRefetchSelected]);

  const selectAllFailedItems = useCallback(async () => {
    const asyncCall = () =>
      fetchAllStacks([
        {
          field: StackSuggestions.State,
          exclude: null,
          constraint: {
            booleanEquals: null,
            enumEquals: [StackState.Failed],
            stringEquals: null,
            stringMatches: null,
            hierarchyNodeValueEquals: null,
            hierarchyNode: null,
            timeInRange: null,
            timeInLast: null,
          },
        },
      ]);

    await asyncCall();
    setRefetchSelected(asyncCall);
  }, [fetchAllStacks, setRefetchSelected]);

  const selectAllUnconfirmedItems = useCallback(async () => {
    const asyncCall = () =>
      fetchAllStacks([
        {
          field: StackSuggestions.State,
          exclude: null,
          constraint: {
            booleanEquals: null,
            enumEquals: [StackState.Unconfirmed],
            stringEquals: null,
            stringMatches: null,
            hierarchyNodeValueEquals: null,
            hierarchyNode: null,
            timeInRange: null,
            timeInLast: null,
          },
        },
      ]);

    await asyncCall();
    setRefetchSelected(asyncCall);
  }, [fetchAllStacks, setRefetchSelected]);

  const selectionOptions = useMemo(
    () => [
      {
        title: "On this page",
        onSelect: addVisibleItems,
      },
      {
        title: "All",
        onSelect: selectAllItems,
      },
      {
        title: "All failed",
        onSelect: selectAllFailedItems,
      },
      {
        title: "All unconfirmed",
        onSelect: selectAllUnconfirmedItems,
      },
    ],
    [addVisibleItems, selectAllItems, selectAllFailedItems, selectAllUnconfirmedItems]
  );

  // sync stacksMap with newly polled visibleStacks
  useEffect(() => {
    if (visibleStacks.length === 0) return;

    setStacksMap((prev) => {
      const newMap = new Map(prev);
      visibleStacks.forEach((stack) => {
        newMap.set(stack.id, stack);
      });
      return newMap;
    });
  }, [visibleStacks]);

  return {
    selectionOptions,
    stacksMap,
  };
};
